"use client";

import { useEffect, useState } from "react";
import { Button } from "./button";
import { Input } from "./input";
import { cn } from "../../lib/utils";

/** Short professional credentials (no long honorific dropdown). */
export const PROFESSIONAL_TITLE_ABBREVS = [
  "MD",
  "DO",
  "NP",
  "PA",
];

const ABBREV_SET = new Set(PROFESSIONAL_TITLE_ABBREVS);

/**
 * @param {string} [value] — stored profile title
 * @param {(next: string) => void} onChange — called with "" or chosen / custom title
 */
export function ProfessionalTitlePicker({
  id,
  value,
  onChange,
  className,
  disabled = false,
}) {
  const normalized = value == null ? "" : String(value).trim();
  const isAbbrev = ABBREV_SET.has(normalized);
  const [customDraft, setCustomDraft] = useState(() =>
    normalized && !isAbbrev ? normalized : ""
  );

  useEffect(() => {
    if (normalized && !ABBREV_SET.has(normalized)) setCustomDraft(normalized);
    else if (ABBREV_SET.has(normalized)) setCustomDraft("");
  }, [normalized]);

  const pickAbbrev = (abbr) => {
    setCustomDraft("");
    onChange(abbr);
  };

  const commitCustom = () => {
    const t = customDraft.trim();
    if (!t) {
      onChange("");
      return;
    }
    onChange(t.slice(0, 24));
  };

  return (
    <div className={cn("flex flex-wrap items-center gap-2", className)}>
      {PROFESSIONAL_TITLE_ABBREVS.map((abbr) => (
        <Button
          key={abbr}
          type="button"
          variant={normalized === abbr ? "default" : "outline"}
          size="sm"
          disabled={disabled}
          className="h-9 min-w-[2.5rem] px-2.5 text-xs font-semibold sm:text-sm"
          onClick={() => pickAbbrev(abbr)}
        >
          {abbr}
        </Button>
      ))}
      <Input
        id={id}
        type="text"
        disabled={disabled}
        className="h-9 w-[6.5rem] sm:w-28 text-xs sm:text-sm"
        placeholder="Other"
        value={customDraft}
        onChange={(e) => {
          const t = e.target.value.replace(/[^\w.\-]/g, "");
          setCustomDraft(t.slice(0, 24));
        }}
        onBlur={commitCustom}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commitCustom();
          }
        }}
      />
    </div>
  );
}
